import { useIntl } from '@capsicum/common';
import { Button, TextInput } from '@capsicum/common-web';
import { PasswordClosedIcon, PasswordIcon } from '@capsicum/common-web/icons';
import { useUserAuthValidateMutation, WithdrawalEvent } from '@capsicum/features/wallet/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { object, string } from 'zod';

import { S } from './Password.styled';

export interface FormWithPasswordInputProps {
	isLoading?: boolean;
	onWithdraw: () => void;
	validationEvent?: WithdrawalEvent;
}

export const FormWithPasswordInput = ({
	isLoading,
	onWithdraw,
	validationEvent = WithdrawalEvent.TransferOut
}: FormWithPasswordInputProps) => {
	const { $t } = useIntl();

	const [showPassword, setShowPassword] = useState<boolean>(false);

	const schema = useMemo(() => {
		return object({
			password: string().nonempty($t('wallet.withdraw.password.required'))
		});
	}, [$t]);

	const form = useForm<{ password: string }>({
		mode: 'onChange',
		resolver: zodResolver(schema)
	});

	const { isPending: isValidatingPassword, mutate: validateAuth } = useUserAuthValidateMutation();

	const texts = {
		confirmButton: $t('wallet.withdraw.password.buttonConfirm'),
		fieldError: $t('wallet.withdraw.password.error'),
		fieldText: $t('wallet.withdraw.password.fieldText')
	};

	const onFormSubmission = (data: { password: string }) => {
		validateAuth(
			{
				event: validationEvent,
				method: 'password',
				secret: data.password
			},
			{
				onError: () => form.setError('password', { message: texts.fieldError, type: 'string' }),
				onSuccess: () => {
					onWithdraw();
				}
			}
		);
	};

	return (
		<form
			onSubmit={form.handleSubmit(data => {
				form.formState.isValid && onFormSubmission(data);
			})}
		>
			<FormProvider {...form}>
				<S.FormContainer>
					<TextInput<{ password: string }>
						iconEnd={{
							dim: !showPassword,
							icon: showPassword ? PasswordIcon : PasswordClosedIcon,
							onClick: () => {
								setShowPassword(!showPassword);
							}
						}}
						label={texts.fieldText}
						name="password"
						testID="withdraw-password-textInput"
						type={showPassword ? 'text' : 'password'}
					/>
					<S.ButtonSection>
						<Button
							disabled={isLoading || !form.formState.isValid}
							fullWidth
							isLoading={isLoading || isValidatingPassword}
							testID="withdraw-password-confirmButton"
							text={texts.confirmButton}
						/>
					</S.ButtonSection>
				</S.FormContainer>
			</FormProvider>
		</form>
	);
};
